import { computed, reactive, ref, watch } from 'vue'
import { createSharedComposable } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { sortBy } from 'lodash-es'
import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import {
  type AssignmentV2_GetUserAssignmentConversationsResponse as AssignmentConversationsResponse,
  AssignmentV2_GetUserAssignmentConversationsResponse_ConversationStatus as Status,
  type AssignmentV2_GetUserAssignmentConversationsResponse_TicketListItem as AssignmentTaskItem,
  type AssignmentV2_UserAssignment,
} from '@zendesk/zqa-services/assignments_v2'

import analytics from '@/utils/analytics'
import { bus } from '@/utils/bus'
import { getCalibrationSessions as apiGetCalibrationSessions, type CalibrationSession } from '@/api/calibration'
import { session } from '@/composables/useSession'
import useReviewSidebar from '@/composables/useReviewSidebar/useReviewSidebar'
import useTicketRouteParams from '@/composables/useTicketRouteParams'
import { getAssignmentConversations } from '@/modules/tasks/api'
import { useAssignmentsQuery } from '@/modules/tasks/queries/useAssignmentsQuery'
import { analyticsLabels, type SortValues } from '@/modules/tasks/utils/assignmentOptions'
import { defaultState, type State } from '@/modules/tasks/types'
import { hasWorkspaceRole, hasAccountRole } from '@/utils/roleUtils'
import { getCalibrationConversations } from '@/modules/shared/TicketContent/api/calibration'
import type { TicketListItemExt } from '@/modules/conversations/types'
import useTasksList, { ListType } from '@/modules/tasks/composables/useTasksList'

interface StoragePayload {
  conversationId: string
  connectionId: string | number
  allowedReviewees?: string[]
}

dayjs.extend(isToday)

export const TASKS_STORAGE_KEY = 'klausExtensionAssignments:v2'
// Keeping reviewees in a separate storage to avoid conflicts with old assignments; these can be consolidated in the future
export const TASKS_REVIEWEES_STORAGE_KEY = 'klausExtensionAssignmentsReviewees:v1'

export default createSharedComposable(() => {
  const route = useRoute()
  const router = useRouter()
  const { state: sidebarState } = useReviewSidebar()
  const { connectionId, conversationId, assignmentId, calibrationSessionId, reviewId } = useTicketRouteParams()
  const { state: listState, isAssignmentsList, isCalibrationsList } = useTasksList()
  const isTasksRoute = computed(() => Boolean(route.name?.toString().startsWith('tasks')))
  const shouldFetchAssignments = computed(() =>
    Boolean(route.name?.toString().startsWith('tasks') && session.workspace?.id),
  )
  const isTasksAssignmentsRoute = computed(() => Boolean(route.name?.toString().startsWith('tasks.assignments')))
  const isTasksExtensionRoute = computed(() => Boolean(route.name?.toString().startsWith('extension.tasks')))
  const isTasksCalibrationsRoute = computed(() => Boolean(route.name?.toString().startsWith('tasks.calibrations')))

  const canSeeReviewerQA = computed(() => {
    const canManage =
      hasWorkspaceRole('MANAGER', 'LEAD', 'REVIEWER') ||
      (!session.workspace?.role && hasAccountRole('ADMIN', 'MANAGER'))
    return session.features.gradeTheReviewerDev && canManage
  })

  const state = reactive<State>(defaultState)
  const {
    data: assignmentsData,
    isFetched: assignmentsFetched,
    refetch: refetchAssignments,
  } = useAssignmentsQuery({ enabled: shouldFetchAssignments })

  const allAssignments = computed(() => assignmentsData.value?.assignments || [])
  const allAgentQAAssignments = computed(() => allAssignments.value.filter((item) => item.settings.type === 'AGENT_QA'))
  const allReviewerQAAssignments = computed(() => {
    if (!canSeeReviewerQA.value) return []

    return allAssignments.value.filter((item) => item.settings.type === 'REVIEWER_QA')
  })
  const previousTicket = computed(() => getTicketByIndex(-1))
  const nextTicket = computed(() => getTicketByIndex(1))
  const statusesWithoutActions = [Status.REMOVED, Status.DONE]

  const isAssignmentTasksView = computed(() =>
    Boolean(route.name?.toString().startsWith('tasks.assignments') && !!assignmentId.value),
  )
  const canCreateAssignmentReview = computed(() => {
    if (!conversationId.value || !listState.items.length || !sidebarState.ticket?.externalId) return false

    const conversation = (listState.items as AssignmentTaskItem[]).find(
      (conv) => conv.externalId === conversationId.value,
    )

    if (statusesWithoutActions.includes(conversation?.ticketStatus?.status)) return false

    if (state.activeAssignment?.settings?.goal?.config?.assigned)
      return conversation?.assignedReviewer?.id === session.user.id.toString()

    return true
  })

  const currFinished = ref<AssignmentV2_UserAssignment[]>([])
  const excludeCurrFinished = (a: AssignmentV2_UserAssignment) => !currFinished.value.includes(a)
  // An assignment with no conversations yet: backend sets goal=0, reviewCount=0.
  // It is neither active (work to do) nor completed (goal reached), so it is treated separately.
  const isNoConversationAssignment = (a: AssignmentV2_UserAssignment) => a.goal === 0 && a.reviewCount === 0

  const activeAssignmentCycles = computed(() => {
    const allAssignments = [
      ...currFinished.value.filter((a) => a?.settings?.type === 'AGENT_QA'),
      ...(allAgentQAAssignments.value
        .filter((a) => a.reviewCount !== a.goal && !isNoConversationAssignment(a))
        .filter(excludeCurrFinished) || []),
    ]

    return sortBy(allAssignments, 'cycleEnd')
  })

  const completedAssignmentCycles = computed(
    () =>
      allAgentQAAssignments.value.filter((a) => a.goal > 0 && a.reviewCount === a.goal).filter(excludeCurrFinished) ||
      [],
  )

  const noConversationAssignmentCycles = computed(() =>
    sortBy(allAgentQAAssignments.value.filter(isNoConversationAssignment).filter(excludeCurrFinished), 'cycleEnd'),
  )

  const activeReviewerQAAssignmentCycles = computed(() => {
    const allAssignments = [
      ...currFinished.value.filter((a) => a.settings?.type === 'REVIEWER_QA'),
      ...(allReviewerQAAssignments.value
        .filter((a) => a.reviewCount !== a.goal && !isNoConversationAssignment(a))
        .filter(excludeCurrFinished) || []),
    ]

    return sortBy(allAssignments, 'cycleEnd')
  })

  const completedReviewerQAAssignmentCycles = computed(
    () =>
      allReviewerQAAssignments.value
        .filter((a) => a.goal > 0 && a.reviewCount === a.goal)
        .filter(excludeCurrFinished) || [],
  )

  const noConversationReviewerQAAssignmentCycles = computed(() =>
    sortBy(allReviewerQAAssignments.value.filter(isNoConversationAssignment).filter(excludeCurrFinished), 'cycleEnd'),
  )

  const canSeeCalibrations = computed(() => {
    const hasRequiredRole =
      hasWorkspaceRole('MANAGER', 'LEAD', 'REVIEWER') ||
      (!session.workspace?.role && hasAccountRole('ADMIN', 'MANAGER'))
    const settingEnabled = session.workspace.settings.calibrationEnabled

    return hasRequiredRole && settingEnabled
  })
  const calibrationSessionDetails = computed(
    () =>
      state.allCalibrationSessions?.find(({ sessionId }) => sessionId.toString() === calibrationSessionId.value) || {
        title: '',
        dueAt: '',
        createdBy: null,
        createdAt: '',
        updatedBy: null,
        updatedAt: '',
      } /* default session */,
  )
  const activeSessions = computed(() =>
    state.allCalibrationSessions?.filter(
      (session) => dayjs(session.dueAt).isAfter(dayjs(), 'day') || dayjs(session.dueAt).isToday(),
    ),
  )
  const pastSessions = computed(() =>
    state.allCalibrationSessions?.filter((session) => dayjs(session.dueAt).isBefore(dayjs(), 'day')),
  )

  function getConversationRoute({ params, query }) {
    const ticketParams = {
      connectionId: params.connectionId,
      conversationId: params.conversationId,
      ...(params.messageId ? { messageId: encodeURIComponent(params.messageId) } : {}),
      ...(params.reviewId ? { reviewId: encodeURIComponent(params.reviewId) } : {}),
    }

    const routeName = route.name?.toString() || ''
    let name: string
    if (routeName.startsWith('extension')) {
      name = 'extension.tasks'
    } else if (isAssignmentsList.value) {
      name = isReviewerQAAssignment.value ? 'tasks.assignments.reviewer-qa.review' : 'tasks.assignments.review'
    } else name = 'tasks.calibrations.review'

    return {
      name,
      params: {
        ...ticketParams,
        ...(isCalibrationsList.value
          ? { calibrationSessionId: encodeURIComponent(params.calibrationSessionId) }
          : { assignmentId: encodeURIComponent(params.assignmentId) }),
      },
      query: { ...query, workspace: session.workspace.id },
    }
  }

  function navigateToTicket(ticket: AssignmentTaskItem | TicketListItemExt, evt?: KeyboardEvent | MouseEvent) {
    if (ticket) {
      router.push(
        getConversationRoute({
          params: {
            connectionId: ticket.paymentTokenId,
            conversationId: ticket.externalId,
            ...(isCalibrationsList.value
              ? { calibrationSessionId: calibrationSessionId.value }
              : { assignmentId: assignmentId.value }),
            ...(isReviewerQAAssignment.value ? { reviewId: ticket.reviewId } : {}),
          },
          query: route.query,
        }),
      )
      if (evt) analytics.shortcutUsed('key' in evt ? evt.key : 'click')
    }
  }

  function clearRoute() {
    if ((conversationId.value || connectionId.value) && route.name?.toString().startsWith('tasks.')) {
      const emptyRoute = {
        name: route.name,
        params: {
          ...(isAssignmentsList.value
            ? { assignmentId: assignmentId.value }
            : { calibrationSessionId: calibrationSessionId.value }),
        },
        query: route.query,
      }
      router.replace(emptyRoute)
    }
  }

  function redirectToConversation() {
    const isCorrectRoute =
      ((isAssignmentsList.value && state.activeAssignment?.id === assignmentId.value) ||
        (isCalibrationsList.value && state.activeCalibrationSession?.sessionId === calibrationSessionId.value)) &&
      conversationId.value === listState.items[0]?.externalId &&
      connectionId.value === Number(listState.items[0]?.paymentTokenId)

    if (isCorrectRoute && listState.items.length) return

    const hasActiveContext = isAssignmentsList.value
      ? Boolean(state.activeAssignment?.id)
      : Boolean(state.activeCalibrationSession?.sessionId)

    if (!hasActiveContext || !listState.items.length) {
      listState.isLoading = false
      sidebarState.loadingFeedback = false
      clearRoute()
      return
    }

    // Check if current conversation exists in the list
    const isCurrentConversationInList =
      conversationId.value &&
      connectionId.value &&
      listState.items.some(
        (item: AssignmentTaskItem | TicketListItemExt) =>
          item.externalId === conversationId.value && item.paymentTokenId === connectionId.value?.toString(),
      )

    const firstTicketRoute = getConversationRoute({
      params: {
        ...(isAssignmentsList.value
          ? { assignmentId: state.activeAssignment?.id }
          : { calibrationSessionId: state.activeCalibrationSession?.sessionId }),
        connectionId: isCurrentConversationInList
          ? connectionId.value
          : Number(listState.items[0]?.paymentTokenId) || undefined,
        conversationId: isCurrentConversationInList
          ? conversationId.value
          : listState.items[0]?.externalId || undefined,
        ...(isReviewerQAAssignment.value
          ? { reviewId: isCurrentConversationInList ? reviewId.value : listState.items[0]?.reviewId }
          : {}),
      },
      query: route.query,
    })

    // Check if we're still on a tasks route before redirecting
    if (firstTicketRoute && route.name?.toString().startsWith('tasks.')) {
      router.replace(firstTicketRoute)
    }
  }

  function getTicketByIndex(indexModifier: number) {
    if (!listState.items.length) return

    const index =
      listState.items.findIndex(
        (t: AssignmentTaskItem | TicketListItemExt) =>
          t.externalId === conversationId.value && t.paymentTokenId === connectionId.value?.toString(),
      ) + indexModifier

    return index !== -1 && listState.items[index]
  }

  function navigateToPreviousTicket(evt: KeyboardEvent | MouseEvent) {
    navigateToTicket(previousTicket.value, evt)
  }

  function navigateToNextTicket(evt?: KeyboardEvent | MouseEvent) {
    navigateToTicket(nextTicket.value, evt)
  }

  async function _getConversations() {
    const activeAssignmentId = assignmentId.value || state.activeAssignment?.id
    const isAssignmentsRoute = isTasksAssignmentsRoute.value || isTasksExtensionRoute.value
    if (!activeAssignmentId || !isAssignmentsRoute) return

    assignmentSeed.value = ''
    state.listParams.seed = ''
    const { data } = await refetchAssignmentConversations({ throwOnError: true })

    if (!isAssignmentsRoute || activeAssignmentId !== (assignmentId.value || state.activeAssignment?.id)) return

    const pages = data?.pages || []
    listState.items = pages.flatMap((page) => page.conversations) as AssignmentTaskItem[]
    listState.total = String(pages[0]?.total ?? 0)
    assignmentSeed.value = pages.at(-1)?.seed || ''
    state.listParams.seed = assignmentSeed.value
    sidebarState.loadingFeedback = false
  }

  async function sortAssignments(sortValue: SortValues) {
    state.listParams.sort = sortValue
    state.listParams.page = 0

    await _getConversations()
    analytics.assignmentListSorted(analyticsLabels[sortValue])
  }

  function setLoadingState(setTicketAsLoading?: boolean) {
    listState.isLoading = true
    if (setTicketAsLoading) bus.$emit('ticket-show-loader')
    sidebarState.loadingFeedback = true
  }

  const isReviewerQAAssignment = computed(
    () => state.activeAssignment?.settings?.type === 'REVIEWER_QA' && route.name?.toString().startsWith('tasks.'),
  )
  const getEmptyAssignmentConversationsResponse = (): AssignmentConversationsResponse => ({
    conversations: [],
    total: 0,
    seed: '',
    sort: state.listParams.sort,
    limit: String(state.listParams.limit),
    offset: '0',
  })
  const assignmentSeed = ref('')
  const {
    data: assignmentConversationsData,
    refetch: refetchAssignmentConversations,
    fetchNextPage: fetchNextAssignmentConversationsPage,
    hasNextPage: hasNextAssignmentConversationsPage,
    isFetchingNextPage: isFetchingNextAssignmentConversationsPage,
  } = useInfiniteQuery({
    queryKey: [
      'tasks-assignment-conversations',
      assignmentId,
      isReviewerQAAssignment,
      computed(() => ({
        limit: state.listParams.limit,
        revieweeId: state.listParams.revieweeId,
        botRevieweeId: state.listParams.botRevieweeId,
        reviewerId: state.listParams.reviewerId,
        reviewed: state.listParams.reviewed,
        sort: state.listParams.sort,
        status: state.listParams.status,
      })),
    ],
    enabled: false,
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const activeAssignmentId = assignmentId.value || state.activeAssignment?.id

      if (!activeAssignmentId) return getEmptyAssignmentConversationsResponse()

      return getAssignmentConversations(
        activeAssignmentId,
        {
          ...state.listParams,
          page: pageParam,
          seed: pageParam === 0 ? '' : assignmentSeed.value,
        },
        isReviewerQAAssignment.value,
      )
    },
    getNextPageParam: (lastPage, pages) => {
      const total = Number(lastPage.total)
      const fetched = pages.flatMap((page) => page.conversations).length

      return !lastPage.conversations.length || fetched >= total ? undefined : pages.length
    },
  })

  watch(
    assignmentConversationsData,
    () => {
      const pages = assignmentConversationsData.value?.pages
      if (!pages) return

      listState.items = pages.flatMap((page) => page.conversations) as AssignmentTaskItem[]
      listState.total = String(pages[0]?.total ?? 0)
      assignmentSeed.value = pages.at(-1)?.seed || ''
      state.listParams.seed = assignmentSeed.value
      sidebarState.loadingFeedback = false
    },
    { immediate: true },
  )

  function getDefaultAssignment(assignments = allAssignments.value) {
    const agentQAAssignment = assignments.find(
      (item) => item.settings.type === 'AGENT_QA' && item.reviewCount !== item.goal,
    )

    if (agentQAAssignment) return agentQAAssignment

    if (!canSeeReviewerQA.value) return undefined
    return assignments.find((item) => item.settings.type === 'REVIEWER_QA' && item.reviewCount !== item.goal)
  }

  async function getAllAssignments(setActive = false) {
    setLoadingState(setActive)

    const { data } = await refetchAssignments({ throwOnError: true })
    const assignments = data?.assignments || []

    const sorted = sortBy(assignments, 'cycleEnd')
    const firstAssignment = getDefaultAssignment(sorted)

    if (!assignments.length) {
      listState.isLoading = false
      sidebarState.loadingFeedback = false
      return
    }
    if (setActive && assignmentId.value) setActiveAssignment(assignmentId.value, assignments)
    else state.activeAssignment = firstAssignment
  }

  function setActiveAssignment(assignmentId: string, assignments = allAssignments.value) {
    const newAssignment = assignments.find((a) => a.id === assignmentId)

    if (newAssignment && newAssignment.id !== state.activeAssignment?.id) {
      state.activeAssignment = newAssignment
    }
  }

  async function getAssignmentConversationsList(setTicketAsLoading = false) {
    if (!assignmentId.value && !state.activeAssignment?.id) return

    state.listParams.page = 0

    if (!listState.isLoading) setLoadingState(setTicketAsLoading)

    if (setTicketAsLoading) state.changedListItem = []

    // Clear items right before fetching to show empty state with correct context
    listState.items = []

    await _getConversations()

    listState.isLoading = false
    sidebarState.loadingFeedback = false
  }

  function _setActiveCalibrationSession(calibSessionId: string) {
    const newSession = state.allCalibrationSessions?.find((a) => a.sessionId === calibSessionId)

    if (newSession) state.activeCalibrationSession = newSession
  }

  async function getCalibrationSessions(calibSessionId?: string): Promise<CalibrationSession[]> {
    if (!canSeeCalibrations.value) return []

    const targetCalibrationSessionId = calibSessionId
    const { data } = await apiGetCalibrationSessions(session.workspace.id)

    state.allCalibrationSessions = data

    if (targetCalibrationSessionId && targetCalibrationSessionId !== calibrationSessionId.value) return data

    if (targetCalibrationSessionId) _setActiveCalibrationSession(targetCalibrationSessionId)
    else state.activeCalibrationSession = data[0]

    return data
  }

  async function getCalibrationConversationList(setTicketAsLoading = true, sessionId = calibrationSessionId.value) {
    if (!sessionId) return

    if (!listState.isLoading) setLoadingState(setTicketAsLoading)

    // Clear items right before fetching to show empty state with correct context
    listState.items = []

    const { tickets } = await getCalibrationConversations({ sessionId })

    if (sessionId !== calibrationSessionId.value) return

    listState.items = tickets || []
    listState.total = String(tickets.length)
    listState.isLoading = false
    sidebarState.loadingFeedback = false
  }

  function loadNextPage() {
    fetchNextAssignmentConversationsPage()
  }

  function getDefaultReviewee(activeConversation: AssignmentTaskItem, assigneeId: number) {
    const { assignedReviewees, assignedBotReviewees } = activeConversation

    if (assignedReviewees?.length) return Number(assignedReviewees[0].id)
    if (assignedBotReviewees?.length) return assignedBotReviewees[0].id

    return assigneeId
  }

  function setDefaultReviewee() {
    if (!conversationId.value || !listState.items?.length) return

    const activeConversation = (listState.items as AssignmentTaskItem[])?.find(
      (item) => item.externalId === conversationId.value,
    )
    const assigneeId =
      sidebarState.ticket?.assignee?.externalId === activeConversation?.assigneeId
        ? Number(sidebarState.ticket?.assignee?.internalId)
        : null

    if (activeConversation) {
      sidebarState.defaultReviewee = getDefaultReviewee(activeConversation, assigneeId)

      if (route.name?.toString().startsWith('tasks.calibrations')) {
        sidebarState.assignmentRevieweeIds = []
        return
      }

      sidebarState.assignmentRevieweeIds = [
        assigneeId || null,
        ...(activeConversation.assignedReviewees.length
          ? activeConversation.assignedReviewees.map((r) => Number(r.id))
          : []),
      ].filter((i) => i)
    }
  }

  function _getStorageKey(connectionId: number | string, conversationId: string) {
    return [session.user.id, session.account.id, session.workspace.id, connectionId, conversationId].join('.')
  }

  function persistAssignment({ conversationId, connectionId, allowedReviewees }: StoragePayload) {
    const savedAssignments = JSON.parse(sessionStorage[TASKS_STORAGE_KEY] || '{}')
    const savedReviewees = JSON.parse(sessionStorage[TASKS_REVIEWEES_STORAGE_KEY] || '{}')

    savedAssignments[_getStorageKey(connectionId, conversationId)] = assignmentId.value
    savedReviewees[assignmentId.value] = allowedReviewees

    sessionStorage[TASKS_STORAGE_KEY] = JSON.stringify(savedAssignments)
    sessionStorage[TASKS_REVIEWEES_STORAGE_KEY] = JSON.stringify(savedReviewees)
  }

  function removePersistedAssignment() {
    if (!sessionStorage[TASKS_STORAGE_KEY]) return

    const savedAssignments = JSON.parse(sessionStorage[TASKS_STORAGE_KEY])
    const key = _getStorageKey(connectionId.value, conversationId.value)

    if (key in savedAssignments) {
      delete savedAssignments[key]
      sessionStorage[key] = JSON.stringify(savedAssignments)
    }
  }

  watch(
    () => [listState.items, listState.isLoading],
    () => {
      if (!route.name?.toString().startsWith('tasks')) return

      // Only hide loader when we're done loading
      if (!listState.isLoading) {
        bus.$emit('ticket-hide-loader')
      }

      if (listState.items?.length) setDefaultReviewee()
    },
    { immediate: true, deep: true },
  )

  watch(
    () => sidebarState.ticket?.assignee,
    () => route.name?.toString().startsWith('tasks') && setDefaultReviewee(),
  )

  watch(
    [() => route.name, assignmentId, assignmentsData],
    async () => {
      if (!isTasksAssignmentsRoute.value && !isTasksExtensionRoute.value) {
        state.activeAssignment = undefined
        return
      }

      state.changedListItem = []
      listState.type = ListType.Assignment
      state.activeCalibrationSession = undefined
      currFinished.value = []

      if (!assignmentsFetched.value || !assignmentsData.value) {
        if (!listState.isLoading) setLoadingState(Boolean(assignmentId.value))
        return
      }

      const assignments = assignmentsData.value.assignments

      if (!assignmentId.value) {
        const firstAssignment = getDefaultAssignment(assignments)

        if (!firstAssignment) {
          listState.isLoading = false
          sidebarState.loadingFeedback = false

          if (canSeeCalibrations.value) {
            return router.push({
              name: 'tasks.calibrations',
            })
          }

          return
        }

        if (isTasksExtensionRoute.value) {
          const result = await router.replace({ name: 'extension.tasks', params: { assignmentId: firstAssignment.id } })
          state.activeAssignment = firstAssignment
          return result
        }

        const result = await router.replace({
          name: firstAssignment.settings.type === 'REVIEWER_QA' ? 'tasks.assignments.reviewer-qa' : 'tasks.assignments',
          params: { assignmentId: firstAssignment.id },
        })
        state.activeAssignment = firstAssignment
        return result
      }

      if (assignmentId.value !== state.activeAssignment?.id) {
        setActiveAssignment(assignmentId.value, assignments)
      }
    },
    { immediate: true },
  )

  watch(
    [() => route.name, () => calibrationSessionId.value],
    async () => {
      if (!isTasksRoute.value) return

      const routeName = route.name
      const targetCalibrationSessionId = calibrationSessionId.value

      await getCalibrationSessions(targetCalibrationSessionId)

      if (route.name !== routeName || calibrationSessionId.value !== targetCalibrationSessionId) return

      if (!isTasksCalibrationsRoute.value) return

      state.changedListItem = []
      listState.type = ListType.Calibration
      state.activeAssignment = undefined

      if (!calibrationSessionId.value) {
        return router.replace({
          name: 'tasks.calibrations',
          params: { calibrationSessionId: state.activeCalibrationSession?.sessionId },
        })
      }

      await getCalibrationConversationList(true, targetCalibrationSessionId)

      if (route.name !== routeName || calibrationSessionId.value !== targetCalibrationSessionId) return

      redirectToConversation()
    },
    { immediate: true },
  )

  return {
    /* all */
    state,
    isAssignmentTasksView,
    canCreateAssignmentReview,
    loadNextPage,
    hasNextPage: hasNextAssignmentConversationsPage,
    isFetchingNextPage: isFetchingNextAssignmentConversationsPage,
    previousTicket,
    nextTicket,
    navigateToNextTicket,
    navigateToPreviousTicket,
    redirectToConversation,
    currFinished,
    /* assignments */
    allAssignments,
    allAgentQAAssignments,
    allReviewerQAAssignments,
    activeAssignmentCycles,
    completedAssignmentCycles,
    noConversationAssignmentCycles,
    sortAssignments,
    getAllAssignments,
    getAssignmentConversationsList,
    statusesWithoutActions,
    setActiveAssignment,
    activeReviewerQAAssignmentCycles,
    completedReviewerQAAssignmentCycles,
    noConversationReviewerQAAssignmentCycles,
    isReviewerQAAssignment,

    /* extension */
    persistAssignment,
    removePersistedAssignment,
    /* calibrations */
    activeSessions,
    pastSessions,
    canSeeCalibrations,
    calibrationSessionDetails,
    getCalibrationSessions,
    getCalibrationConversationList,
  }
})
